import definitions from '@/constants/companyLabels.json';
import type {DefaultMeta} from '@/types';

/**
 * The host's own words for customers, orders, quotes and agreements, plus the word
 * for a mixed fleet — `settings.labels.*` company settings. Mirrors
 * app/Support/CompanyLabels.php (a parity test keeps them in sync).
 */
export type CompanyLabelKey = 'customer' | 'order' | 'quote' | 'agreement' | 'listing';

export interface CompanyLabelDefinition {
    title: string;
    question: string;
    default: string | null;
    options: Record<string, [string, string]>;
}

export const COMPANY_LABEL_META_PREFIX = 'settings.labels.';
export const COMPANY_LABELS = definitions as unknown as Record<CompanyLabelKey, CompanyLabelDefinition>;
export const COMPANY_LABEL_KEYS = Object.keys(COMPANY_LABELS) as CompanyLabelKey[];

export interface CompanyLabels {
    /** The chosen option key, or null when a mixed-fleet word was never picked. */
    choice: (key: CompanyLabelKey) => string | null;
    /** "reservation" */
    singular: (key: CompanyLabelKey) => string;
    /** "reservations" */
    plural: (key: CompanyLabelKey) => string;
    /** "Reservation" */
    Singular: (key: CompanyLabelKey) => string;
    /** "Reservations" */
    Plural: (key: CompanyLabelKey) => string;
    /** The host's mixed-fleet word, or null to keep each screen's default. */
    mixedFleetWords: () => MixedFleetWords | null;
}

export interface MixedFleetWords {
    item: string;
    Item: string;
    items: string;
    Items: string;
}

const lowerFirst = (value: string): string => (value ? value.charAt(0).toLowerCase() + value.slice(1) : value);

export function companyLabels(meta: DefaultMeta | Record<string, unknown> | null | undefined): CompanyLabels {
    const choice = (key: CompanyLabelKey): string | null => {
        const definition = COMPANY_LABELS[key];
        const chosen = meta?.[`${COMPANY_LABEL_META_PREFIX}${key}`];

        return typeof chosen === 'string' && chosen in definition.options ? chosen : definition.default;
    };

    const words = (key: CompanyLabelKey): [string, string] => {
        const chosen = choice(key);

        return chosen ? COMPANY_LABELS[key].options[chosen] : ['', ''];
    };

    return {
        choice,
        singular: (key) => lowerFirst(words(key)[0]),
        plural: (key) => lowerFirst(words(key)[1]),
        Singular: (key) => words(key)[0],
        Plural: (key) => words(key)[1],
        mixedFleetWords: () => {
            if (!choice('listing')) {
                return null;
            }

            const [Item, Items] = words('listing');

            return {item: lowerFirst(Item), Item, items: lowerFirst(Items), Items};
        },
    };
}
